import { type TypedDocumentNode, gql } from '@apollo/client';
import type { ComponentProps } from 'react';

import type { Variant as NewVariant } from '@sorare/core/src/components/rewards/AnyRewardConfig';
import { uniqueBy } from '@sorare/core/src/lib/arrays';
import type { WithRewardRange } from '@sorare/core/src/lib/rewards';

import { RankRow } from 'components/leaderboard/CumulativeRankRewards/CumulativeRankRow';
import { RankRewardTitle } from 'components/leaderboard/CumulativeRankRewards/RankRewardTitle';
import type { RewardRangeVariant } from 'components/leaderboard/RewardRange';
import { RewardRange } from 'components/leaderboard/RewardRange';
import { ClickableAnyRewardConfig } from 'components/rewards/ClickableAnyRewardConfig';

import type {
  RankReward_so5Leaderboard,
  RankReward_so5Ranking,
  RankReward_so5RewardConfig,
} from './__generated__/index.graphql';

type Props = {
  so5Leaderboard: RankReward_so5Leaderboard;
  so5RewardConfig: WithRewardRange<RankReward_so5RewardConfig>;
  so5Ranking?: RankReward_so5Ranking;
  overview: boolean;
  isMyRange: boolean;
  isMyNextRange: boolean;
  rewardRangeVariant: RewardRangeVariant;
  rewardVariant?: NewVariant;
  onClickRanking?: (so5RankingId: string) => void;
  hideRankings?: boolean;
  selectedContender?: string;
  hideRewards?: boolean;
  hideScoreDistance?: boolean;
  displayAlternativeRewards?: boolean;
} & Pick<ComponentProps<typeof RewardRange>, 'withSeparator'>;

export const RankReward = ({
  so5Leaderboard,
  so5RewardConfig,
  so5Ranking,
  overview,
  isMyRange,
  isMyNextRange,
  rewardRangeVariant,
  onClickRanking,
  hideRankings,
  withSeparator,
  rewardVariant,
  selectedContender,
  hideRewards,
  hideScoreDistance,
  displayAlternativeRewards,
}: Props) => {
  const isMyRangeOrMyNextRange = isMyRange || isMyNextRange;
  const shouldDisplay = overview ? isMyRangeOrMyNextRange : true;

  if (!shouldDisplay) {
    return null;
  }

  const scoreDistanceFromMyRanking = so5Ranking
    ? (so5Ranking.score || 0) - (so5RewardConfig.toSo5Ranking?.score || 0)
    : undefined;

  return (
    <RewardRange
      key={so5RewardConfig.startRank}
      variant={rewardRangeVariant}
      title={
        <RankRewardTitle
          {...so5RewardConfig}
          scoreDistanceFromMyRanking={scoreDistanceFromMyRanking}
          hideScoreDistance={hideScoreDistance}
        />
      }
      rewards={
        hideRewards
          ? []
          : (displayAlternativeRewards
              ? so5RewardConfig.alternativeRewardConfigs
              : so5RewardConfig.rewardConfigs
            ).map((rewardConfig, i) => {
              return (
                <ClickableAnyRewardConfig
                  // eslint-disable-next-line react/no-array-index-key
                  key={i}
                  anyRewardConfig={rewardConfig}
                  so5League={so5Leaderboard.so5League}
                  variant={rewardVariant}
                />
              );
            })
      }
      withSeparator={withSeparator}
    >
      {!hideRankings &&
        uniqueBy(
          ranking => (ranking.ranking || 0).toString(),
          [
            so5RewardConfig.fromSo5Ranking,
            isMyRange && so5Ranking,
            so5RewardConfig.toSo5Ranking,
          ].filter(Boolean)
        ).map(ranking => {
          return (
            <RankRow
              key={ranking.ranking}
              ranking={ranking}
              isMe={ranking.ranking === so5Ranking?.ranking}
              isSelected={
                selectedContender ===
                ranking?.so5Lineup.so5LeaderboardContender.slug
              }
              onClick={
                onClickRanking
                  ? () =>
                      onClickRanking?.(
                        ranking.so5Lineup.so5LeaderboardContender.slug
                      )
                  : undefined
              }
              collapsible={false}
            />
          );
        })}
    </RewardRange>
  );
};
RankReward.fragments = {
  so5Leaderboard: gql`
    fragment RankReward_so5Leaderboard on So5Leaderboard {
      slug
      mainRarityType
      so5League {
        slug
        ...ClickableAnyRewardConfig_so5League
      }
    }
    ${ClickableAnyRewardConfig.fragments.so5League}
  ` as TypedDocumentNode<RankReward_so5Leaderboard>,
  so5RewardConfig: gql`
    fragment RankReward_so5RewardConfig on So5RewardConfig {
      id
      ranks
      rankPct
      alternativeRewardConfigs {
        id
        ...ClickableAnyRewardConfig_anyRewardConfigInterface
      }
      rewardConfigs {
        id
        ...ClickableAnyRewardConfig_anyRewardConfigInterface
      }
      fromSo5Ranking {
        id
        ranking
        score
        so5Lineup {
          id
          so5LeaderboardContender {
            slug
          }
        }
        ...RankRow_userRankingInterface
      }
      toSo5Ranking {
        id
        ranking
        score
        so5Lineup {
          id
          so5LeaderboardContender {
            slug
          }
        }
        ...RankRow_userRankingInterface
      }
    }
    ${ClickableAnyRewardConfig.fragments.anyRewardConfigInterface}
    ${RankRow.fragments.userRankingInterface}
  ` as TypedDocumentNode<RankReward_so5RewardConfig>,
  so5Ranking: gql`
    fragment RankReward_so5Ranking on So5Ranking {
      id
      ranking
      score
      so5Lineup {
        id
        so5LeaderboardContender {
          slug
        }
      }
      ...RankRow_userRankingInterface
    }
    ${RankRow.fragments.userRankingInterface}
  ` as TypedDocumentNode<RankReward_so5Ranking>,
};
